import { AlertTriangle, ArrowLeft, Loader2, Megaphone, Rocket } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { CarregandoBloco, CarregandoFormulario, CarregandoGrade } from "@/components/carregando";

import { ROTA_ANUNCIOS } from "../api";
import PreviaAnuncio from "./PreviaAnuncio";
import ResumoPublicar from "./ResumoPublicar";
import SecaoOnde from "./SecaoOnde";
import SecaoOQueAnunciar from "./SecaoOQueAnunciar";
import SecaoQuando from "./SecaoQuando";
import SecaoQuanto from "./SecaoQuanto";
import SecaoTexto from "./SecaoTexto";
import useImpulsionar from "./useImpulsionar";

function Rodape({ a, novoAnuncio, aoCancelar, aoPublicar }) {
  return (
    <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-3" data-e2e="rodape-impulsionar">
      <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-end gap-3">
        {a.problema && !a.publicando && (
          <p className="mr-auto flex items-start gap-1.5 text-sm text-amber-600 min-w-0" data-e2e="impulsionar-problema">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{a.problema}</span>
          </p>
        )}
        <button type="button" onClick={aoCancelar} disabled={a.publicando} data-e2e="cancelar-impulsionar"
          className="h-10 px-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-medium hover:bg-red-500/20 transition disabled:opacity-50">
          Cancelar
        </button>
        <button type="button" onClick={aoPublicar} disabled={!!a.problema || a.publicando} data-e2e="publicar-impulsionar"
          className="h-12 px-7 rounded-lg bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50">
          {a.publicando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
          {a.publicando ? "Publicando…" : novoAnuncio ? "Criar anúncio" : "Publicar campanha"}
        </button>
      </div>
    </div>
  );
}

/**
 * IMPULSIONE → Anúncios Meta → Impulsionar: cria a campanha sem sair do Núcleo
 * (fase 4 do plano). Tela inteira no molde da Nova Despesa: os passos à
 * esquerda, a prévia e o resumo do custo à direita, o Publicar preso embaixo.
 * Com `:campanhaId` na rota vira "novo anúncio na campanha": só imagem e texto.
 */
export default function ImpulsionarPage() {
  const { campanhaId } = useParams();
  const navigate = useNavigate();
  const a = useImpulsionar(campanhaId);
  const [sair, setSair] = useState(false);
  const novoAnuncio = !!campanhaId;

  const voltar = () => navigate(ROTA_ANUNCIOS);
  const cancelar = () => (a.alterado ? setSair(true) : voltar());
  const publicar = async () => { if (await a.publicar()) voltar(); };
  const botao = (a.dados?.botoes || []).find((b) => b.valor === a.form.botao)?.rotulo || "Pedir agora";

  return (
    <div className="h-full flex flex-col overflow-hidden" data-e2e="impulsionar">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={cancelar} aria-label="Voltar para Anúncios Meta"
              className="h-10 w-10 rounded-lg border flex items-center justify-center hover:bg-muted shrink-0"><ArrowLeft className="w-4 h-4" /></button>
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Megaphone className="w-5 h-5 text-primary" /></div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold leading-tight" data-e2e="impulsionar-titulo">{novoAnuncio ? "Novo anúncio na campanha" : "Impulsionar"}</h1>
              <p className="text-sm text-muted-foreground">
                {novoAnuncio
                  ? "Outra foto ou outro texto na mesma campanha: o que já está rodando continua, sem recomeçar o aprendizado."
                  : "Crie o anúncio aqui: ele vai para o Instagram e o Facebook, e os pedidos voltam marcados com a campanha."}
              </p>
            </div>
          </div>

          {a.erro && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-3 text-sm text-red-500">{a.erro}</div>}
          {!a.dados && !a.erro && (
            // Carregamento com cards (25/10/2026): as etapas e a prévia do anúncio piscando.
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] items-start">
              <div className="space-y-4 min-w-0">
                <CarregandoGrade quantos={6} comImagem className="rounded-2xl border bg-card p-4 grid gap-2 grid-cols-3 sm:grid-cols-6" />
                <CarregandoFormulario campos={3} />
              </div>
              <CarregandoBloco altura="h-[520px]" />
            </div>
          )}
          {a.dados && (
            <>
              {!novoAnuncio && a.dados.bloqueios.map((b) => (
                <div key={b.tipo} className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm" data-e2e="impulsionar-bloqueio">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /><span>{b.texto}</span>
                </div>
              ))}
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] items-start">
                <div className="space-y-4 min-w-0">
                  <SecaoOQueAnunciar a={a} />
                  <SecaoTexto a={a} />
                  {!novoAnuncio && <SecaoOnde a={a} />}
                  {!novoAnuncio && <SecaoQuanto a={a} />}
                  {!novoAnuncio && <SecaoQuando a={a} />}
                </div>
                <aside className="space-y-4 lg:sticky lg:top-0">
                  <PreviaAnuncio pagina={a.dados.pagina} imagem={a.imagem} texto={a.form.texto} titulo={a.form.titulo} botao={botao} link={a.dados.link} />
                  {!novoAnuncio && <ResumoPublicar a={a} />}
                </aside>
              </div>
            </>
          )}
        </div>
      </div>
      <Rodape a={a} novoAnuncio={novoAnuncio} aoCancelar={cancelar} aoPublicar={publicar} />

      <AlertDialog open={sair} onOpenChange={setSair}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{novoAnuncio ? "Descartar este anúncio?" : "Descartar esta campanha?"}</AlertDialogTitle>
            <AlertDialogDescription>Nada foi publicado. O que você preencheu será perdido.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={voltar}>Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
